import { forwardRef } from "react";
import { transformImage, transformImageSrcSet, type TransformOpts } from "@/lib/imageUrl";

// Single image primitive for the public, photo-heavy surfaces (listing cards,
// galleries, heroes). It centralizes the three levers that drive LCP / CLS /
// bandwidth for anonymous search visitors, so callsites don't hand-repeat them:
//   • `transform`  → Supabase render-size + webp (never ship the 4000px original)
//   • `srcSetWidths` + `sizes` → responsive/retina so phones don't fetch desktop pixels
//   • `priority`   → above-the-fold LCP image loads eager + fetchpriority=high;
//                    everything else stays lazy + decoding=async
// Non-Supabase URLs pass through transformImage unchanged and simply get no srcset.

type ImgProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> & {
  /** Raw image URL (a Supabase public-object URL, or any URL, passed through). */
  src: string | null | undefined;
  /** Transform for the base/fallback src. Pass the largest size actually rendered. */
  transform?: TransformOpts;
  /** Widths (CSS px) to emit as a responsive srcset. Omit to skip srcset. */
  srcSetWidths?: number[];
  /** `sizes` describing the rendered width across breakpoints (needed with srcset). */
  sizes?: string;
  /** Above-the-fold LCP image → eager + fetchpriority=high. Default false → lazy. */
  priority?: boolean;
};

const Img = forwardRef<HTMLImageElement, ImgProps>(function Img(
  { src, transform, srcSetWidths, sizes, priority = false, alt = "", ...rest },
  ref,
) {
  const resolvedSrc = transformImage(src, transform);
  // srcset entries vary width only (quality/format carried over), no width /
  // height / resize, so Supabase scales proportionally and the <img>'s
  // object-cover handles framing (per imageUrl.ts guidance). "" for non-Supabase.
  const srcSet = srcSetWidths && srcSetWidths.length > 0
    ? transformImageSrcSet(src, srcSetWidths, {
        quality: transform?.quality,
        format: transform?.format,
      })
    : "";

  return (
    <img
      ref={ref}
      {...rest}
      src={resolvedSrc}
      {...(srcSet ? { srcSet, sizes } : {})}
      alt={alt}
      decoding="async"
      loading={priority ? "eager" : "lazy"}
      {...(priority ? { fetchPriority: "high" as const } : {})}
    />
  );
});

export default Img;
